// index.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Flex,
  Box,
  Text,
  Card,
  Badge,
  Button as RadixButton,
} from '@radix-ui/themes';
import {
  CalendarIcon,
  ClockIcon,
  ExitIcon,
  ReloadIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { patientAPI } from '../../utils/api/directus';
import { gsap } from 'gsap';
import styles from './styles.module.scss';

// Countdown for upcoming appointments
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  function calculateTimeLeft(dateString) {
    const now = new Date().getTime();
    const target = new Date(dateString).getTime();
    const difference = target - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
      expired: false,
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (timeLeft.expired) {
    return (
      <div className={styles.countdownContainer}>
        <Text className={styles.countdownLabel}>Appointment time has arrived!</Text>
      </div>
    );
  }

  return (
    <div className={styles.countdownContainer}>
      <Text className={styles.countdownLabel}>Time until appointment</Text>
      <div className={styles.countdownChips}>
        {timeLeft.days > 0 && (
          <div className={styles.countdownChip}>
            <span className={styles.countdownValue}>{timeLeft.days}</span>
            <span className={styles.countdownUnit}>
              day{timeLeft.days !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className={styles.countdownChip}>
          <span className={styles.countdownValue}>
            {String(timeLeft.hours).padStart(2, '0')}
          </span>
          <span className={styles.countdownUnit}>
            hour{timeLeft.hours !== 1 ? 's' : ''}
          </span>
        </div>
        <div className={styles.countdownChip}>
          <span className={styles.countdownValue}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </span>
          <span className={styles.countdownUnit}>min</span>
        </div>
        <div className={styles.countdownChip}>
          <span className={styles.countdownValue}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </span>
          <span className={styles.countdownUnit}>sec</span>
        </div>
      </div>
    </div>
  );
};

const PatientDashboard = () => {
  const { user, patient, logout } = useAuth();
  const navigate = useNavigate();
  
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' | 'history'

  // GSAP refs
  const dashboardRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const upcomingTimelineRef = useRef(null);
  const historyTimelineRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  const fetchPatientData = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const [history, upcoming] = await Promise.all([
        patientAPI.getAppointmentHistory(patient.id),
        patientAPI.getUpcomingAppointments(patient.id),
      ]);

      setAppointmentHistory(history);
      setUpcomingAppointments(upcoming);
    } catch (err) {
      console.error('Error fetching patient data:', err);
      setError('Failed to load patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  // Refresh data when returning from confirmation page
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchPatientData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [fetchPatientData]);

  // Entrance animations - only run once on initial load
  useEffect(() => {
    if (loading || hasAnimatedRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from(dashboardRef.current, {
        opacity: 0,
        duration: 0.35,
        ease: 'power1.out',
      });

      if (headerRef.current) {
        gsap.from(headerRef.current, {
          y: -40,
          opacity: 0,
          duration: 0.7,
          ease: 'power3.out',
        });
      }

      if (statsRef.current) {
        gsap.from(
          statsRef.current.querySelectorAll('[data-animate="stat-pill"]'),
          {
            y: 24,
            opacity: 0,
            duration: 0.6,
            ease: 'power3.out',
            stagger: 0.08,
            delay: 0.1,
          }
        );
      }
    }, dashboardRef);

    hasAnimatedRef.current = true;
    return () => ctx.revert();
  }, [loading]);

  // Animate timeline items when they first appear (only once per list)
  useEffect(() => {
    if (loading) return;
    
    const currentTimelineRef = viewMode === 'upcoming' ? upcomingTimelineRef : historyTimelineRef;
    const list = viewMode === 'upcoming' ? upcomingAppointments : appointmentHistory;
    
    if (currentTimelineRef.current && list.length > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const rows = currentTimelineRef.current.querySelectorAll('[data-animate="appointment-row"]');
        const unanimatedRows = Array.from(rows).filter(row => !row.dataset.animated);

        if (unanimatedRows.length > 0) {
          gsap.from(unanimatedRows, {
            y: 15,
            opacity: 0,
            duration: 0.35,
            ease: 'power2.out',
            stagger: 0.04,
            onComplete: () => {
              unanimatedRows.forEach(row => row.dataset.animated = 'true');
            }
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, viewMode, upcomingAppointments.length, appointmentHistory.length]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      scheduled: 'blue',
      confirmed: 'green',
      completed: 'gray',
      cancelled: 'red',
    };

    return (
      <Badge color={statusColors[status] || 'gray'} className={styles.statusBadge}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const getServiceName = (service) => {
    if (!service) return 'General consultation';
    return service.name || service.Name || 'General consultation';
  };

  const getDoctorName = (doctor) => {
    if (!doctor) return 'Staff';
    return (
      doctor.name ||
      `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() ||
      'Staff'
    );
  };

  const getPatientFullName = () => {
    if (patient) {
      const full = `${patient.first_name || ''} ${
        patient.last_name || ''
      }`.trim();
      if (full) return full;
    }
    if (user) {
      const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (full) return full;
    }
    return 'Patient';
  };

  const getPhoneNumber = () => {
    return patient?.phone || user?.phone || 'Not available';
  };

  const nextAppointment = upcomingAppointments[0] || null;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loaderWrapper}>
        <ReloadIcon className={styles.spinner} />
          <Text className={styles.loadingText}>Loading your dashboard...</Text>
        </div>
      </div>
    );
  }

  const renderAppointmentList = (list, isHistory) => {
    if (!list || list.length === 0) {
      return (
        <div className={styles.emptyState}>
          {isHistory ? (
            <CalendarIcon className={styles.emptyIcon} />
          ) : (
            <ClockIcon className={styles.emptyIcon} />
          )}
          <Text className={styles.emptyTitle}>
            {isHistory ? 'No appointment history' : 'No upcoming appointments'}
          </Text>
          <Text className={styles.emptyText}>
            {isHistory
              ? 'Your completed appointments will appear here.'
              : 'Contact the clinic to schedule your next visit.'}
          </Text>
        </div>
      );
    }

    return (
      <div 
        className={styles.timeline} 
        ref={isHistory ? historyTimelineRef : upcomingTimelineRef}
      >
        {list.map((appointment, index) => (
          <div
            key={appointment.id}
            className={styles.appointmentRow}
            data-animate="appointment-row"
          >
            <div className={styles.timelineRail}>
              <div
                className={
                  isHistory
                    ? styles.appointmentIconPast
                    : styles.appointmentIconUpcoming
                }
              >
                {/* small icon before each appointment */}
                <CalendarIcon className={styles.appointmentIconGlyph} />
              </div>
              {index !== list.length - 1 && <span className={styles.timelineLine} />}
            </div>

            <div className={styles.appointmentContent}>
              <div className={styles.appointmentHeaderRow}>
                <div className={styles.appointmentTitleWrapper}>
                  <Text className={styles.appointmentTitle}>
                    {getServiceName(appointment.service_id)}
                  </Text>
                  <Text className={styles.appointmentDoctor}>
                    with Dr. {getDoctorName(appointment.doctor_id)}
                  </Text>
                </div>
                {getStatusBadge(appointment.status)}
              </div>

              <div className={styles.appointmentMetaRow}>
                <div className={styles.metaPill}>
                  <CalendarIcon className={styles.metaIcon} />
                  <span className={styles.metaText}>
                    {formatDate(appointment.appointment_date)}
                  </span>
                </div>
                <div className={styles.metaPill}>
                  <ClockIcon className={styles.metaIcon} />
                  <span className={styles.metaText}>
                    {formatTime(appointment.appointment_date)}
                  </span>
                </div>
              </div>

              {!isHistory && (
                <CountdownTimer targetDate={appointment.appointment_date} />
              )}

              {appointment.notes && (
                <Box className={styles.notesBlock}>
                  <Text className={styles.notesLabel}>Notes</Text>
                  <Text className={styles.notesText}>{appointment.notes}</Text>
                </Box>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.dashboardContainer} ref={dashboardRef}>
      {/* background blobs */}
      <div className={styles.backgroundElements}>
        <div className={styles.gradientOrb1} />
        <div className={styles.gradientOrb2} />
        <div className={styles.gradientOrb3} />
      </div>

      {/* HEADER */}
      <header className={styles.header} ref={headerRef}>
        <div className={styles.headerContent}>
          <div className={styles.welcomeSection}>
            <div className={styles.welcomeTopRow}>
              <div className={styles.avatarBubble}>
                <span className={styles.avatarInitials}>
                  {getPatientFullName().charAt(0).toUpperCase()}
                </span>
              </div>
              <div className={styles.welcomeTextBlock}>
                <Text className={styles.welcomeGreeting}>Welcome back</Text>
                <Text className={styles.welcomeName}>{getPatientFullName()}</Text>
              </div>
            </div>

            <div className={styles.patientInfo}>
              <div className={styles.infoChip}>
                <PersonIcon className={styles.infoIcon} />
                <Text className={styles.infoText}>{getPatientFullName()}</Text>
              </div>
              <div className={styles.infoChip}>
                <svg
                  className={styles.infoIcon}
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.625 1.5C3.625 1.22386 3.84886 1 4.125 1H10.875C11.1511 1 11.375 1.22386 11.375 1.5V13.5C11.375 13.7761 11.1511 14 10.875 14H4.125C3.84886 14 3.625 13.7761 3.625 13.5V1.5ZM4.625 2V13H10.375V2H4.625ZM6.5 11.5C6.5 11.2239 6.72386 11 7 11H8C8.27614 11 8.5 11.2239 8.5 11.5C8.5 11.7761 8.27614 12 8 12H7C6.72386 12 6.5 11.7761 6.5 11.5Z"
                    fill="currentColor"
                    fillRule="evenodd"
                    clipRule="evenodd"
                  />
                </svg>
                <Text className={styles.infoText}>{getPhoneNumber()}</Text>
              </div>
            </div>

            <Text className={styles.subtitleText}>
              See your next visit at a glance, then scroll through your full history.
            </Text>
          </div>

          <div className={styles.headerRight}>
            {nextAppointment && (
              <Card className={styles.nextAppointmentPanel}>
                <Flex direction="column" gap="2">
                  <Flex justify="between" align="center" gap="2">
                    <Text className={styles.nextLabel}>Next appointment</Text>
                    {getStatusBadge(nextAppointment.status)}
                  </Flex>
                  <Text className={styles.nextTitle}>
                    {getServiceName(nextAppointment.service_id)}
                  </Text>
                  <Text className={styles.nextDoctor}>
                    with Dr. {getDoctorName(nextAppointment.doctor_id)}
                  </Text>

                  <Flex gap="2" wrap="wrap" className={styles.nextMetaRow}>
                    <div className={styles.metaPill}>
                      <CalendarIcon className={styles.metaIcon} />
                      <span className={styles.metaText}>
                        {formatDate(nextAppointment.appointment_date)}
                      </span>
                    </div>
                    <div className={styles.metaPill}>
                      <ClockIcon className={styles.metaIcon} />
                      <span className={styles.metaText}>
                        {formatTime(nextAppointment.appointment_date)}
                      </span>
                    </div>
                  </Flex>
                </Flex>
              </Card>
            )}

          <RadixButton
            variant="outline"
            size="3"
            onClick={handleLogout}
            className={styles.logoutButton}
          >
            <ExitIcon />
            Logout
          </RadixButton>
        </div>
      </div>
      </header>

      {/* ERROR BANNER */}
      {error && (
        <Box className={styles.errorBanner}>
          <Text className={styles.errorText}>{error}</Text>
          <RadixButton
            variant="soft"
            size="2"
            onClick={fetchPatientData}
            className={styles.retryButton}
          >
            <ReloadIcon />
            Retry
          </RadixButton>
        </Box>
      )}

      {/* MAIN CONTENT */}
      <main className={styles.mainContent}>
        {/* stats as soft pills */}
        <section className={styles.statsRow} ref={statsRef}>
          <div className={styles.statPill} data-animate="stat-pill">
            <div className={styles.statLabelRow}>
              <div className={styles.statIconCircle}>
                <CalendarIcon className={styles.statIcon} />
              </div>
              <span className={styles.statLabel}>Total appointments</span>
            </div>
            <span className={styles.statValue}>
                {appointmentHistory.length + upcomingAppointments.length}
            </span>
          </div>

          <div className={styles.statPill} data-animate="stat-pill">
            <div className={styles.statLabelRow}>
              <div className={styles.statIconCircle}>
                <ClockIcon className={styles.statIcon} />
              </div>
              <span className={styles.statLabel}>Upcoming</span>
            </div>
            <span className={styles.statValue}>{upcomingAppointments.length}</span>
        </div>

          <div className={styles.statPill} data-animate="stat-pill">
            <div className={styles.statLabelRow}>
              <div className={styles.statIconCircle}>
                <CalendarIcon className={styles.statIcon} />
              </div>
              <span className={styles.statLabel}>Completed</span>
            </div>
            <span className={styles.statValue}>{appointmentHistory.length}</span>
          </div>
        </section>

        {/* segmented view switch + timeline */}
        <section className={styles.timelineSection}>
          <div className={styles.tabsBar}>
            <div className={styles.viewSwitch}>
              <button
                type="button"
                onClick={() => setViewMode('upcoming')}
                className={`${styles.viewChip} ${
                  viewMode === 'upcoming' ? styles.viewChipActive : ''
                }`}
              >
                <ClockIcon />
                <span>Upcoming</span>
                {upcomingAppointments.length > 0 && (
                  <span className={styles.viewChipBadge}>
                    {upcomingAppointments.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('history')}
                className={`${styles.viewChip} ${
                  viewMode === 'history' ? styles.viewChipActive : ''
                }`}
              >
                <CalendarIcon />
                <span>History</span>
              </button>
                          </div>

            <div className={styles.tabContent}>
              {viewMode === 'upcoming'
                ? renderAppointmentList(upcomingAppointments, false)
                : renderAppointmentList(appointmentHistory, true)}
                </div>
                </div>
        </section>
      </main>
    </div>
  );
};

export default PatientDashboard;
